import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  MesProduct,
  MesProductOption,
  MesProductOptionField,
  MesProductOptionList,
  MesProductPayload,
  MesProductReference,
} from '@/types/cgt'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const body = JSON.parse(text) as { message?: string | string[] }
      // class-validator answers with an array of messages; show them all.
      message = Array.isArray(body.message) ? body.message.join('. ') : (body.message ?? text)
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/**
 * Product Codes (z8nrz7fvhx): the products an MES run makes. Tenant-scoped:
 * authHeaders injects X-Tenant-Id for the selected tenant. Deactivated with
 * `update(id, { active: false })`; there is no delete.
 */
export const mesProductsApi = {
  /** By code. `active` narrows to active (true) or inactive (false) ones. */
  list: (params: { active?: boolean } = {}) =>
    request<MesProduct[]>(`/mes-products${params.active === undefined ? '' : `?active=${params.active}`}`),

  get: (id: string) => request<MesProduct>(`/mes-products/${encodeURIComponent(id)}`),

  /** Each selection field's options, and the storage units. */
  reference: () => request<MesProductReference>('/mes-products/reference'),

  /** The selection fields' own option lists, options with ids, in the spec's order. */
  optionLists: () => request<MesProductOptionList[]>('/mes-products/option-lists'),

  /** Replace one field's list. Products keep the value they hold. */
  updateOptionList: (field: MesProductOptionField, options: MesProductOption[]) =>
    request<MesProductOptionList>(`/mes-products/option-lists/${encodeURIComponent(field)}`, {
      method: 'PUT',
      body: JSON.stringify({ options }),
    }),

  create: (payload: MesProductPayload) =>
    request<MesProduct>('/mes-products', { method: 'POST', body: JSON.stringify(payload) }),

  update: (id: string, payload: MesProductPayload) =>
    request<MesProduct>(`/mes-products/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
}
